import React, { useEffect, useMemo, useState } from "react";
import { XIcon, CheckIcon } from "../icons";
import S from "../styles";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

/**
 * Set what is inside a bundle.
 *
 * A bundle keeps no stock of its own — what you can sell is worked out from
 * what is in it. So there is no "is this a bundle" tick box anywhere: putting
 * the first thing in makes it one, taking the last thing out un-makes it, and
 * nobody can land on a bundle with nothing in it.
 *
 * The count is per member, unlike the hardware modal's single shared box: a
 * 5-pack of small plates is 5 of one thing, but a both-amps set is 1 + 1.
 */
export default function EditBundleModal({ product, store, storeName, onClose, onSaved }) {
  const [products, setProducts] = useState([]);
  const [saved, setSaved] = useState({}); // memberId -> qty already stored
  const [picked, setPicked] = useState({}); // memberId -> qty, this session
  const [insideOther, setInsideOther] = useState(false);
  const [search, setSearch] = useState("");
  const [setAll, setSetAll] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // A bundle can not also be the row that carries a family's shared stock —
  // it holds none. The server refuses it, so say so before anything is ticked.
  const inStockGroup = !!product.baseProduct;
  const blocked = inStockGroup || insideOther;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    // The whole store's bundle rows in one call: it gives both what is in this
    // one and whether this product is already inside something else.
    Promise.all([
      fetch(`${API_URL}/api/products?store=${store}`).then((r) => r.json()),
      fetch(`${API_URL}/api/product-bundles?store=${store}`).then((r) => r.json()),
    ])
      .then(([prods, rows]) => {
        if (!alive) return;
        const links = Array.isArray(rows) ? rows : [];
        const map = {};
        links.filter((m) => m.bundleId === product.id).forEach((m) => { map[m.memberId] = m.qty || 1; });
        setProducts(Array.isArray(prods) ? prods : []);
        setSaved(map);
        setPicked({ ...map });
        setInsideOther(links.some((m) => m.memberId === product.id));
        setLoading(false);
      })
      .catch(() => { if (alive) { setErr("Could not load products."); setLoading(false); } });
    return () => { alive = false; };
  }, [store, product.id]);

  // Stock lives on ONE row per family — the lowest id of the singles sharing a
  // base product, which is the rule the server uses to pick the same row.
  const poolOf = useMemo(() => {
    const holders = {};
    [...products].sort((a, b) => a.id - b.id).forEach((p) => {
      if (p.baseProduct && (p.packSize || 1) === 1 && holders[p.baseProduct] === undefined) {
        holders[p.baseProduct] = p.quantity || 0;
      }
    });
    return (p) => (p.baseProduct ? holders[p.baseProduct] || 0 : p.quantity || 0);
  }, [products]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    // A bundle can not hold itself, and bundles are flat — nothing that is
    // already a bundle can go inside one.
    const list = products.filter((p) => p.id !== product.id && !p.isBundle);
    const hit = q ? list.filter((p) => `${p.sku || ""} ${p.name || ""}`.toLowerCase().includes(q)) : list;
    // What is already in it floats to the top, then alphabetical by SKU.
    return [...hit].sort((a, b) => {
      const sa = saved[a.id] !== undefined ? 0 : 1;
      const sb = saved[b.id] !== undefined ? 0 : 1;
      if (sa !== sb) return sa - sb;
      return String(a.sku || a.name || "").localeCompare(String(b.sku || b.name || ""));
    });
  }, [products, search, saved, product.id]);

  const pickedIds = Object.keys(picked).map(Number);
  const changed = pickedIds.filter((id) => saved[id] !== picked[id]);
  const removed = Object.keys(saved).map(Number).filter((id) => picked[id] === undefined);
  const dirty = changed.length > 0 || removed.length > 0;

  const toggle = (id) => setPicked((s) => {
    const next = { ...s };
    if (next[id] !== undefined) delete next[id];
    else next[id] = saved[id] || 1;
    return next;
  });

  const setQty = (id, v) => setPicked((s) => ({ ...s, [id]: Math.max(1, parseInt(v, 10) || 1) }));

  const applySetAll = () => {
    const n = Math.max(1, parseInt(setAll, 10) || 1);
    setPicked((s) => Object.keys(s).reduce((o, k) => ({ ...o, [k]: n }), {}));
  };

  const toggleAllShown = () => {
    const allOn = shown.length > 0 && shown.every((p) => picked[p.id] !== undefined);
    setPicked((s) => {
      const next = { ...s };
      shown.forEach((p) => { if (allOn) delete next[p.id]; else if (next[p.id] === undefined) next[p.id] = 1; });
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setErr("");
    const who = (() => {
      try { return (JSON.parse(sessionStorage.getItem("nycmfg-user")) || {}).name || ""; } catch { return ""; }
    })();
    try {
      if (pickedIds.length === 0) {
        // Emptying it is not an error — the product simply stops being a
        // bundle and gets its own stock number back.
        const r = await fetch(`${API_URL}/api/product-bundles/bulk-unlink`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ store, bundleId: product.id, productIds: Object.keys(saved).map(Number) }),
        });
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || "Could not empty it.");
      } else {
        const r = await fetch(`${API_URL}/api/product-bundles/bulk`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            store, bundleId: product.id, createdBy: who,
            members: pickedIds.map((id) => ({ productId: id, qty: picked[id] })),
          }),
        });
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || "Could not save.");
      }
      onSaved(pickedIds.length);
      onClose();
    } catch (e) {
      setErr(e.message || "Something went wrong.");
      setSaving(false);
    }
  };

  const cell = { ...S.td, padding: "7px 12px" };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(10,10,15,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
    >
      <div style={{ background: "#F4F2EC", border: "3px solid #0A0A0F", width: "min(900px, 96vw)", maxHeight: "88vh", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "16px 20px", borderBottom: "2px solid #0A0A0F", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div>
            <div style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 20, fontWeight: 900, color: "#0A0A0F", textTransform: "uppercase", letterSpacing: "-0.01em" }}>
              What is in {product.name}?
            </div>
            <div style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", marginTop: 2 }}>
              {storeName} · {pickedIds.length === 0
                ? "Nothing yet — this is a plain product"
                : `${pickedIds.length} thing${pickedIds.length !== 1 ? "s" : ""} in one bundle`}
            </div>
          </div>
          <button onClick={onClose} disabled={saving} style={{ ...S.iconBtn, fontSize: 16 }}><XIcon /></button>
        </div>

        {blocked && (
          <div style={{ padding: "10px 20px", borderBottom: "2px solid #0A0A0F", background: "rgba(194,65,15,0.08)", color: "#C2410C", fontSize: 13, flexShrink: 0 }}>
            {inStockGroup
              ? `This product shares stock with the “${product.baseProduct}” group, so it can not also be a bundle. Clear its Base Product on the Products page first.`
              : "This product is already inside another bundle, so it can not hold things itself. Bundles are one level deep."}
          </div>
        )}

        <div style={{ padding: "14px 20px", borderBottom: "2px solid #0A0A0F", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", flexShrink: 0 }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search SKU or name"
            autoFocus
            style={{ ...S.input, marginBottom: 0, flex: "1 1 240px" }}
          />
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "rgba(10,10,15,0.58)", whiteSpace: "nowrap" }}>
            Set all ticked to
            <input
              type="number"
              min="1"
              value={setAll}
              onChange={(e) => setSetAll(e.target.value)}
              placeholder="5"
              style={{ ...S.input, marginBottom: 0, width: 70, textAlign: "center", fontFamily: "'JetBrains Mono', monospace" }}
            />
            <button onClick={applySetAll} disabled={!setAll || pickedIds.length === 0} style={{ ...S.clearBtn, opacity: !setAll || pickedIds.length === 0 ? 0.5 : 1 }}>Apply</button>
          </label>
          <button onClick={toggleAllShown} style={{ ...S.clearBtn, whiteSpace: "nowrap" }}>
            {shown.length > 0 && shown.every((p) => picked[p.id] !== undefined) ? "Untick shown" : `Tick all ${shown.length}`}
          </button>
        </div>

        <div style={{ overflowY: "auto", flex: 1 }}>
          {loading && <div style={{ padding: 40, textAlign: "center", color: "rgba(10,10,15,0.58)" }}>Loading products…</div>}
          {!loading && shown.length === 0 && (
            <div style={{ padding: 40, textAlign: "center", color: "rgba(10,10,15,0.58)" }}>
              {search ? `No product matches "${search}".` : "Nothing else in this store to put in it."}
            </div>
          )}
          {!loading && shown.length > 0 && (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={{ ...S.th, width: 46 }} />
                  <th style={{ ...S.th, width: "24%" }}>SKU</th>
                  <th style={S.th}>Product</th>
                  <th style={{ ...S.th, width: 90, textAlign: "center" }}>In stock</th>
                  <th style={{ ...S.th, width: 96, textAlign: "center" }}>How many</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((p) => {
                  const on = picked[p.id] !== undefined;
                  return (
                    <tr
                      key={p.id}
                      onClick={() => toggle(p.id)}
                      style={{ cursor: "pointer", background: on ? "rgba(0,51,255,0.07)" : undefined }}
                    >
                      <td style={{ ...cell, textAlign: "center" }}>
                        <span style={{
                          display: "inline-flex", width: 20, height: 20, border: "2px solid #0A0A0F",
                          alignItems: "center", justifyContent: "center",
                          background: on ? "#0033FF" : "transparent", color: "#fff",
                        }}>
                          {on ? <CheckIcon /> : null}
                        </span>
                      </td>
                      <td style={{ ...cell, fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}>{p.sku || "—"}</td>
                      <td style={{ ...cell, fontSize: 13 }}>
                        {p.name}
                        {(p.packSize || 1) > 1 && (
                          <span style={{ color: "#0033FF", fontSize: 12, marginLeft: 6, fontFamily: "'JetBrains Mono', monospace" }}>
                            {p.packSize}-pack
                          </span>
                        )}
                      </td>
                      <td style={{ ...cell, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "rgba(10,10,15,0.58)" }}>
                        {poolOf(p)}
                      </td>
                      <td style={{ ...cell, textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                        {on ? (
                          <input
                            type="number"
                            min="1"
                            value={picked[p.id]}
                            onChange={(e) => setQty(p.id, e.target.value)}
                            style={{ ...S.cellInput, width: 62, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 13, padding: "4px 6px" }}
                          />
                        ) : (
                          <span style={{ color: "rgba(10,10,15,0.35)" }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ padding: "14px 20px", borderTop: "2px solid #0A0A0F", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", flexShrink: 0 }}>
          <div style={{ fontSize: 13, color: err ? "#C2410C" : "rgba(10,10,15,0.58)" }}>
            {err
              ? err
              : pickedIds.length === 0 && Object.keys(saved).length > 0
                ? "This will stop being a bundle and get its own stock number back."
                : dirty
                  ? `${changed.length} to set${removed.length ? `, ${removed.length} to take out` : ""}`
                  : "No changes"}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={onClose} disabled={saving} style={S.clearBtn}>Cancel</button>
            <button onClick={save} disabled={saving || !dirty || blocked} style={{ ...S.addBtn, opacity: saving || !dirty || blocked ? 0.5 : 1 }}>
              {saving ? "Saving…" : "Save bundle"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
